import axios from 'axios';
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Stack,
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { PasswordInput } from '../components/form/inputs';

interface LocationState {
  from?: string;
  message?: string;
}

const LOGIN_PAGE_VERSION = 'v1.0.0';

export const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState) ?? {};

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);

    try {
      await login({ username, password });
      const nextPath = state.from ?? '/admin';
      navigate(nextPath, { replace: true });
    } catch (error) {
      let message = 'Não foi possível entrar. Verifique suas credenciais.';
      if (axios.isAxiosError(error)) {
        const detail =
          (error.response?.data as { detail?: string; error?: string })?.detail ??
          (error.response?.data as { error?: string })?.error;
        if (detail) {
          message = detail;
        }
      } else if (error instanceof Error && error.message === 'ACCESS_TOKEN_MISSING') {
        message = 'Resposta inválida do servidor.';
      }
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="py-5 bg-light">
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow border-0">
              <Card.Body className="p-4">
                <Stack gap={3}>
                  <div>
                    <Card.Title>Entrar</Card.Title>
                    <Card.Subtitle className="text-muted">
                      Demonstração de autenticação com níveis de acesso.
                    </Card.Subtitle>
                  </div>

                  {state.message && (
                    <Alert variant="warning" className="mb-0">
                      {state.message}
                    </Alert>
                  )}

                  {errorMessage && (
                    <Alert variant="danger" className="mb-0">
                      {errorMessage}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Stack gap={3}>
                      <Form.Group controlId="loginUsername">
                        <Form.Label>Usuário ou e-mail</Form.Label>
                        <Form.Control
                          type="text"
                          value={username}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setUsername(event.target.value)
                          }
                          placeholder="admin ou user@empresa.com"
                          autoComplete="username"
                          required
                        />
                      </Form.Group>

                      <PasswordInput
                        controlId="loginPassword"
                        label="Senha"
                        value={password}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          setPassword(event.target.value)
                        }
                        required
                        placeholder="••••••"
                        autoComplete="current-password"
                      />

                      <Button
                        type="submit"
                        size="lg"
                        disabled={submitting}
                        className="mt-2"
                      >
                        Entrar
                      </Button>
                    </Stack>
                  </Form>
                </Stack>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <small className="position-fixed bottom-0 end-0 text-muted pe-3 pb-2 opacity-75">
        {LOGIN_PAGE_VERSION}
      </small>
    </main>
  );
};
